import { FormEvent, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/AuthContext";

const REMEMBERED_USERNAME_KEY = "ws.rememberedUsername";

function readRememberedUsername(): string {
  try {
    return localStorage.getItem(REMEMBERED_USERNAME_KEY) ?? "";
  } catch {
    return "";
  }
}

export default function LoginPage() {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [rememberedUsername] = useState(readRememberedUsername);
  const [username, setUsername] = useState(rememberedUsername);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(rememberedUsername.length > 0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // already authed → kick to next dest
  if (status === "authed") {
    const next = (location.state as { from?: string } | null)?.from || "/";
    navigate(next, { replace: true });
    return null;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const normalizedUsername = username.trim();
      await login(normalizedUsername, password, remember);
      try {
        if (remember) localStorage.setItem(REMEMBERED_USERNAME_KEY, normalizedUsername);
        else localStorage.removeItem(REMEMBERED_USERNAME_KEY);
      } catch {
        /* Browser storage may be disabled; login itself still succeeded. */
      }
      const next = (location.state as { from?: string } | null)?.from || "/";
      navigate(next, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "登录失败");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-lg border bg-white dark:bg-zinc-900 p-6 shadow-sm space-y-4"
      >
        <div className="text-center">
          <div className="text-2xl">🐋</div>
          <div className="text-lg font-semibold tracking-wide mt-1">WhaleSeek 寻鲸</div>
          <div className="text-xs text-zinc-500 mt-1">登录后使用</div>
        </div>
        <label className="block">
          <span className="text-xs text-zinc-600 dark:text-zinc-400">用户名</span>
          <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            autoComplete="username"
            className="mt-1 w-full rounded border px-3 py-2 bg-white dark:bg-zinc-800 text-sm"
            required
          />
        </label>
        <label className="block">
          <span className="text-xs text-zinc-600 dark:text-zinc-400">密码</span>
          <span className="relative mt-1 block">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              className="w-full rounded border px-3 py-2 pr-16 bg-white dark:bg-zinc-800 text-sm"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-0 px-3 text-xs text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
              aria-label={showPassword ? "隐藏密码" : "显示密码"}
              aria-pressed={showPassword}
            >
              {showPassword ? "隐藏" : "显示"}
            </button>
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400 select-none">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4 rounded border-zinc-300"
          />
          <span>记住我（30 天）</span>
        </label>
        {error && (
          <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-300 rounded px-2 py-1">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={submitting || !username || !password}
          className="w-full rounded bg-zinc-900 text-white dark:bg-white dark:text-zinc-900 py-2 text-sm font-medium disabled:opacity-50"
        >
          {submitting ? "登录中…" : "登录"}
        </button>
        <div className="text-center text-xs text-zinc-400 space-y-1">
          <div>有邀请码？<Link to="/register" className="text-blue-600 hover:underline">去注册</Link></div>
          <div>忘记密码？让 owner 去 DB 改 password_hash。</div>
        </div>
      </form>
    </div>
  );
}
